<template>
	<div
		class="g-header-info-card-v2-preview"
		:class="{
			'g-header-info-card-v2-preview--h5': h5,
			'g-header-info-card-v2-preview--compact': compact,
		}"
		:style="rootStyle"
	>
		<div
			ref="carousel"
			class="g-header-info-card-v2-preview__viewport"
			@touchstart.passive="onTouchStart"
			@touchmove="onTouchMove"
			@touchend="onTouchEnd"
			@touchcancel="onTouchEnd"
		>
			<div
				class="g-header-info-card-v2-preview__track"
				:class="{ 'is-dragging': isTouchDragging }"
				:style="trackStyle"
			>
				<div
					v-for="(slide, index) in slides"
					:key="slide.id"
					class="g-header-info-card-v2-preview__slide"
					@click="onSlideClick(slide, index)"
				>
					<img
						v-if="getImageUrl(slide)"
						:src="getImageUrl(slide)"
						class="g-header-info-card-v2-preview__bg"
						alt=""
					/>
					<div v-else class="g-header-info-card-v2-preview__empty">
						<i class="mbm-iconfont mbm-tupian"></i>
					</div>
					<div
						v-if="slide.title || slide.subtitle"
						class="g-header-info-card-v2-preview__text"
					>
						<div
							v-if="slide.title"
							class="g-header-info-card-v2-preview__title"
							:style="{ color: normalizedData.titleColor }"
						>{{ slide.title }}</div>
						<div
							v-if="slide.subtitle"
							class="g-header-info-card-v2-preview__subtitle"
							:style="{ color: normalizedData.subtitleColor }"
						>{{ slide.subtitle }}</div>
					</div>
				</div>
			</div>
		</div>

		<div
			v-if="slides.length > 1"
			class="g-header-info-card-v2-preview__dots"
		>
			<span
				v-for="(slide, index) in slides"
				:key="`dot-${slide.id}`"
				class="g-header-info-card-v2-preview__dot"
				:class="{ 'is-active': index === activeIndex }"
				:style="getDotStyle(index)"
				@click.stop="setActiveIndex(index)"
			></span>
		</div>

		<div
			class="g-header-info-card-v2-preview__edge"
			:style="{ background: normalizedData.edgeColor }"
		></div>
	</div>
</template>

<script>
	import { navigateJumpLink } from '../utils/jumpLink.js';
	import {
		getHeaderInfoCardV2ImageUrl,
		HEADER_INFO_CARD_V2_COMPACT_HEIGHT,
		HEADER_INFO_CARD_V2_DEFAULT_EDGE_SIZE,
		normalizeHeaderInfoCardV2Data,
	} from '../utils/headerInfoCardV2.js';

	const SWIPE_MIN_PX = 40;
	const SWIPE_RATIO = 0.18;

	export default {
		name: 'GHeaderInfoCardV2Preview',
		props: {
			data: {
				type: Object,
				default: () => ({}),
			},
			h5: {
				type: Boolean,
				default: false,
			},
			compact: {
				type: Boolean,
				default: false,
			},
			interactive: {
				type: Boolean,
				default: true,
			},
		},
		data() {
			return {
				activeIndex: 0,
				touchStartX: 0,
				touchStartY: 0,
				isTouchDragging: false,
				dragOffset: 0,
				carouselWidth: 0,
				autoplayTimer: null,
			};
		},
		computed: {
			normalizedData() {
				return normalizeHeaderInfoCardV2Data(this.data);
			},
			slides() {
				return this.normalizedData.items;
			},
			rootStyle() {
				const unit = this.h5 ? 'rem' : 'px';
				const scale = this.h5 ? 1 / 100 : 1;
				const height = this.compact
					? HEADER_INFO_CARD_V2_COMPACT_HEIGHT
					: this.normalizedData.height;
				const edge = HEADER_INFO_CARD_V2_DEFAULT_EDGE_SIZE * (this.compact ? 0.65 : 1);
			return {
				'--g-card-height': `${height * scale}${unit}`,
				'--g-edge-size': `${edge * scale}${unit}`,
				'--g-card-ratio': this.h5 ? `${538} / ${height}` : null,
			};
			},
			canSwipe() {
				return this.interactive && this.slides.length > 1;
			},
			trackStyle() {
				const baseOffset = this.activeIndex * 100;
				if (!this.isTouchDragging || !this.carouselWidth) {
					return { transform: `translateX(-${baseOffset}%)` };
				}
				return {
					transform: `translateX(calc(-${baseOffset}% + ${this.dragOffset}px))`,
				};
			},
		},
		watch: {
			data: {
				handler() {
					this.activeIndex = 0;
					this.resetTouchState();
					this.syncIndexBounds();
					this.restartAutoplay();
				},
			},
			slides: {
				handler() {
					this.syncIndexBounds();
				},
			},
			'normalizedData.autoplay'() {
				this.restartAutoplay();
			},
			'normalizedData.autoplayInterval'() {
				this.restartAutoplay();
			},
		},
		mounted() {
			this.syncIndexBounds();
			this.restartAutoplay();
		},
		beforeUnmount() {
			this.stopAutoplay();
		},
		methods: {
			getImageUrl(slide) {
				return getHeaderInfoCardV2ImageUrl(slide?.image);
			},
			getDotStyle(index) {
				const isActive = index === this.activeIndex;
				const color = isActive
					? this.normalizedData.dotActiveColor
					: this.normalizedData.dotColor;
				if (this.h5) {
					return {
						background: color,
						width: '0.28rem',
						height: '0.08rem',
						borderRadius: '0.04rem',
					};
				}
				return {
					background: color,
					width: this.compact ? '10px' : '14px',
					height: this.compact ? '3px' : '4px',
					borderRadius: '2px',
				};
			},
			setActiveIndex(index) {
				if (!this.interactive) return;
				const max = Math.max(0, this.slides.length - 1);
				this.activeIndex = Math.min(Math.max(0, index), max);
				this.restartAutoplay();
			},
			syncIndexBounds() {
				const max = Math.max(0, this.slides.length - 1);
				if (this.activeIndex > max) {
					this.activeIndex = max;
				}
			},
			onSlideClick(slide) {
				if (!this.h5 || !this.interactive || !slide?.jumpLink?.type) return;
				navigateJumpLink(slide.jumpLink, {
					apiRequest: this.$apiRequest,
				});
			},
			getCarouselWidth() {
				return this.$refs.carousel?.offsetWidth || 0;
			},
			onTouchStart(event) {
				if (!this.canSwipe) return;
				this.stopAutoplay();
				const touch = event.touches[0];
				this.touchStartX = touch.clientX;
				this.touchStartY = touch.clientY;
				this.isTouchDragging = false;
				this.dragOffset = 0;
				this.carouselWidth = this.getCarouselWidth();
			},
			onTouchMove(event) {
				if (!this.canSwipe) return;
				const touch = event.touches[0];
				const deltaX = touch.clientX - this.touchStartX;
				const deltaY = touch.clientY - this.touchStartY;
				if (!this.isTouchDragging) {
					if (Math.abs(deltaX) < 8) return;
					if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
					this.isTouchDragging = true;
				}
				event.preventDefault();
				let offset = deltaX;
				if (this.activeIndex === 0 && offset > 0) {
					offset *= 0.35;
				} else if (this.activeIndex === this.slides.length - 1 && offset < 0) {
					offset *= 0.35;
				}
				this.dragOffset = offset;
			},
			onTouchEnd() {
				if (!this.canSwipe) {
					this.resetTouchState();
					this.restartAutoplay();
					return;
				}
				if (this.isTouchDragging) {
					const threshold = Math.max(SWIPE_MIN_PX, this.carouselWidth * SWIPE_RATIO);
					if (this.dragOffset < -threshold && this.activeIndex < this.slides.length - 1) {
						this.setActiveIndex(this.activeIndex + 1);
					} else if (this.dragOffset > threshold && this.activeIndex > 0) {
						this.setActiveIndex(this.activeIndex - 1);
					}
				}
				this.resetTouchState();
				this.restartAutoplay();
			},
			resetTouchState() {
				this.isTouchDragging = false;
				this.dragOffset = 0;
			},
			stopAutoplay() {
				if (this.autoplayTimer) {
					clearInterval(this.autoplayTimer);
					this.autoplayTimer = null;
				}
			},
			restartAutoplay() {
				this.stopAutoplay();
				if (!this.interactive || !this.normalizedData.autoplay || this.slides.length <= 1) {
					return;
				}
				this.autoplayTimer = setInterval(() => {
					const next = (this.activeIndex + 1) % this.slides.length;
					this.setActiveIndex(next);
				}, this.normalizedData.autoplayInterval);
			},
		},
	};
</script>

<style scoped lang="less">
	.g-header-info-card-v2-preview {
		position: relative;
		width: 100%;
		height: var(--g-card-height, 360px);
		overflow: hidden;
		box-sizing: border-box;
		/* 整体底部浅 V，与蓝条外轮廓对齐 */
		clip-path: polygon(
			0 0,
			100% 0,
			100% calc(100% - var(--g-edge-size, 36px) * 0.55),
			50% 100%,
			0 calc(100% - var(--g-edge-size, 36px) * 0.55)
		);
	}

	.g-header-info-card-v2-preview__viewport {
		position: absolute;
		inset: 0;
		z-index: 0;
		overflow: hidden;
		touch-action: pan-x pinch-zoom;
	}

	.g-header-info-card-v2-preview__track {
		display: flex;
		height: 100%;
		transition: transform 0.25s ease;

		&.is-dragging {
			transition: none;
		}
	}

	.g-header-info-card-v2-preview__slide {
		position: relative;
		flex: 0 0 100%;
		min-width: 0;
		height: 100%;
		overflow: hidden;
	}

	.g-header-info-card-v2-preview__bg {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		clip-path: polygon(0% 0%, 0 0, 0% calc(100% - 38px), 50% 100%, 100% calc(100% - 38px), 100% 0%, 100% 0%);
	}

	.g-header-info-card-v2-preview__empty {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: #e8eaed;
		color: #bfbfbf;
		font-size: 36px;
	}

	.g-header-info-card-v2-preview__text {
		position: absolute;
		left: 0;
		right: 0;
		top: 20%;
		z-index: 1;
		display: flex;
		flex-direction: column;
		align-items: center;
		padding: 0 20px;
		text-align: center;
		pointer-events: none;
	}

	.g-header-info-card-v2-preview__title {
		font-size: 28px;
		font-weight: 700;
		line-height: 1.35;
		word-break: break-word;
		text-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
	}

	.g-header-info-card-v2-preview__subtitle {
		margin-top: 8px;
		font-size: 16px;
		font-weight: 400;
		line-height: 1.4;
		word-break: break-word;
		text-shadow: 0 1px 3px rgba(0, 0, 0, 0.16);
	}

	/* 指示点：压在蓝条上方、右下角 */
	.g-header-info-card-v2-preview__dots {
		position: absolute;
		right: 12px;
		bottom: calc(var(--g-edge-size, 36px) + 10px);
		z-index: 3;
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 5px;
	}

	.g-header-info-card-v2-preview__dot {
		display: inline-block;
		flex-shrink: 0;
		cursor: pointer;
	}

	/* 蓝条：顶边水平直线，底边浅 V，贴容器最底 */
	.g-header-info-card-v2-preview__edge {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		z-index: -1;
		height: var(--g-edge-size, 36px);
		clip-path: polygon(0 0, 100% 0, 100% 45%, 50% 100%, 0 45%);
		pointer-events: none;
	}

	.g-header-info-card-v2-preview--h5 {
		aspect-ratio: var(--g-card-ratio, 538 / 360);

		.g-header-info-card-v2-preview__bg {
			clip-path: polygon(0% 0%, 0 0, 0% calc(100% - .45rem), 50% 100%, 100% calc(100% - .45rem), 100% 0%, 100% 0%);
		}
		.g-header-info-card-v2-preview__text {
			top: 20%;
			padding: 0 0.4rem;
		}

		.g-header-info-card-v2-preview__title {
			font-size: 0.44rem;
		}

		.g-header-info-card-v2-preview__subtitle {
			margin-top: 0.1rem;
			font-size: 0.28rem;
		}

		.g-header-info-card-v2-preview__dots {
			right: 0.24rem;
			bottom: calc(var(--g-edge-size, 0.36rem) + 0.16rem);
		}

		.g-header-info-card-v2-preview__empty {
			font-size: 0.56rem;
		}
	}

	.g-header-info-card-v2-preview--compact {
		.g-header-info-card-v2-preview__text {
			top: 20%;
			padding: 0 12px;
		}

		.g-header-info-card-v2-preview__title {
			font-size: 15px;
		}

		.g-header-info-card-v2-preview__subtitle {
			margin-top: 4px;
			font-size: 12px;
		}

		.g-header-info-card-v2-preview__dots {
			right: 8px;
			bottom: calc(var(--g-edge-size, 24px) + 6px);
		}

		.g-header-info-card-v2-preview__empty {
			font-size: 24px;
		}
	}
</style>
